<script setup lang="ts">
import useAuthUser from "@/composables/useAuthUser";
import useUserFilters from "@/modules/users/composables/useUserFilters";
import useUsers from "@/modules/users/composables/useUsers";
import { useVuelidate } from "@vuelidate/core";
import { helpers, required } from "@vuelidate/validators";
import VueDatePicker from "@vuepic/vue-datepicker";
import { computed, onBeforeMount, reactive, ref } from "vue";
import { useToast } from "vue-toastification";
import useCard from "../composables/useCard";

import HasPermission from "@/components/HasPermission.vue";
import StyledButton from "@/components/StyledButton.vue";
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  TransitionRoot,
} from "@headlessui/vue";
import { CheckIcon, ChevronUpDownIcon } from "@heroicons/vue/20/solid";

const { users } = useUsers({ immediate: false });
const { setFiltersValue } = useUserFilters();
const { user } = useAuthUser();
const { createCardAsync, activeRefetch, createCardLoading } = useCard();

interface Form {
  number: string;
  fullName: string;
  expMonth: number;
  expYear: number;
  expDate: string | any;
  cvv: string;
  brand: string;
  cardeableId: string | any;
}

const form = reactive<Form>({
  number: "",
  fullName: "",
  expMonth: 0,
  expYear: 0,
  expDate: "",
  cvv: "",
  brand: "",
  cardeableId: "",
});

const rules = {
  number: {
    required: helpers.withMessage(
      "El numero de tarjeta debe tener 15 - 16 digitos.",
      required,
    ),
  },
  expDate: {
    required: helpers.withMessage(
      "La fecha de expiración es obligatorio.",
      required,
    ),
  },
  cvv: { required: helpers.withMessage("El cvv es obligatorio.", required) },
  cardeableId: {
    required: helpers.withMessage(
      "El dueño de la tarjeta es obligatorio.",
      required,
    ),
  },
};

const query = ref("");
const v$ = useVuelidate(rules, form);
const toast = useToast();
const isBackVisible = ref(false);

const toggleBackCard = () => {
  isBackVisible.value = !isBackVisible.value;
};

const filterUsers = computed(() =>
  query.value === ""
    ? users.value
    : users.value?.filter((user: any) => {
        return (
          user.name?.toLowerCase().includes(query.value.toLowerCase()) ||
          user.lastname?.toLowerCase().includes(query.value.toLowerCase())
        );
      }),
);

const handleSubmit = async () => {
  for (const field of v$.value.$silentErrors) {
    if (!Array.isArray(field.$message)) {
      toast.error(field.$message);
      return;
    }
  }

  if (!v$.value.$invalid) {
    form.fullName = form.cardeableId.fullName;
    form.cardeableId = form.cardeableId.id;
    form.brand = typeCard.value.type;
    form.expMonth = form.expDate.month + 1;
    form.expYear = form.expDate.year;

    await createCardAsync({
      params: form,
      config: {
        onSuccessToast: "Tarjeta guardada",
        raiseToast: true,
      },
    });

    form.number = "";
    form.fullName = "";
    form.expMonth = 0;
    form.expYear = 0;
    form.expDate = "";
    form.cvv = "";
    form.brand = "";
    form.cardeableId = "";
    await activeRefetch();
  }
};

onBeforeMount(async () => {
  setFiltersValue("company", user?.value?.companyId);
});

const numberLength = computed(() => {
  return form.number.startsWith("3") ? 15 : 16;
});

const userName = computed(() => {
  if (form.cardeableId && form.cardeableId.name && form.cardeableId.lastname) {
    return `${form.cardeableId.name} ${form.cardeableId.lastname}`;
  } else {
    return "";
  }
});

const cvvLength = computed(() => {
  return form.number.startsWith("3") ? 4 : 3;
});
const typeCard = computed(() => {
  let urlLogoCard = "";
  let type = "";

  if (form.number.startsWith("3")) {
    urlLogoCard =
      "https://www.kreiconceptstore.com/media/wysiwyg/american-express.png";
    type = "Amex";
  } else if (form.number.startsWith("4")) {
    urlLogoCard =
      "https://1000marcas.net/wp-content/uploads/2019/12/VISA-Logo.png";
    type = "Visa";
  } else {
    urlLogoCard =
      "https://download.logo.wine/logo/Mastercard/Mastercard-Logo.wine.png";
    type = "MasterCard";
  }

  return { urlLogoCard, type };
});
</script>

<template>
  <!-- data-screenshot-exclude: dato de tarjeta fuera de la captura del widget de bugs (ver useScreenCapture.ts). Aqui el numero y el CVV van en campos type="text". -->
  <div class="flex flex-col items-center" data-screenshot-exclude>
    <h1 class="mb-3 font-extrabold text-3xl uppercase">Nueva tarjeta</h1>

    <div class="w-full flex flex-col items-center justify-between">
      <form
        @submit.prevent="handleSubmit"
        class="bg-white w-full mx-auto px-4 lg:px-6 py-8 shadow-md rounded-md flex flex-col items-center"
        autocomplete="off"
      >
        <div
          class="w-96 h-56 rounded-xl relative text-white shadow-2xl mb-8"
          :class="{
            'rotate-y-180 transition-transform duration-500': isBackVisible,
            'transition-transform duration-500': !isBackVisible,
          }"
          @click="toggleBackCard"
        >
          <div
            class="front w-full h-full absolute top-0 left-0 bg-red-100"
            :class="{ hidden: isBackVisible }"
          >
            <img
              class="relative object-cover w-full h-full rounded-xl"
              src="https://i.imgur.com/kGkSg1v.png"
            />
            <div class="w-full px-4 absolute top-8">
              <h1 class="font-bold uppercase">Enlaceforte</h1>
              <div class="flex justify-end">
                <img class="w-32 h-14" :src="typeCard.urlLogoCard" />
              </div>
              <div class="flex justify-between mt-12">
                <p class="font-medium">
                  {{ form.number !== "" ? form.number : "xxxx xxxx xxxx xxxx" }}
                </p>
                <p class="font-medium text-sm" v-if="form.expDate">
                  {{
                    form?.expDate !== ""
                      ? form.expDate.month + 1 + "/" + form.expDate.year
                      : "00/00"
                  }}
                </p>

                <p v-else class="font-medium text-sm">
                  {{ "00/00" }}
                </p>
              </div>
              <div>
                <p class="font-medium">{{ userName }}</p>
              </div>
            </div>
          </div>
          <div
            class="back w-full h-full absolute top-0 left-0 transform rotateY-180"
            :class="{ hidden: !isBackVisible }"
          >
            <div class="w-96 h-56 rounded-xl relative text-white shadow-2xl">
              <img
                class="relative object-cover w-full h-full rounded-xl"
                src="https://i.imgur.com/kGkSg1v.png"
              />

              <div class="w-full px-4 absolute top-8">
                <div class="flex justify-center mt-16">
                  <div
                    class="bg-white w-3/4 h-10 rounded text-black text-center my-auto watermark-signature"
                  ></div>
                </div>

                <div class="flex justify-end mt-4">
                  <p
                    class="font-medium text-sm rotate-cvv"
                    :class="{ 'rotate-cvv-visible': isBackVisible }"
                  >
                    CVV: {{ form.cvv !== "" ? form.cvv : "XXX" }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="w-full lg:w-1/2">
          <div class="mb-4">
            <label class="text-neutral-800 font-bold text-sm mb-2 block"
              >Numero tarjeta</label
            >
            <input
              type="text"
              v-model="form.number"
              class="flex h-10 w-full rounded-md border-2 bg-background px-4 py-1.5 text-lg ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:border-purple-600 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              :maxlength="numberLength"
              placeholder="XXXX XXXX XXXX XXXX"
            />
          </div>

          <div class="flex justify-between mb-4">
            <div class="w-1/2">
              <label class="text-neutral-800 font-bold text-sm mb-2 block"
                >Fecha exp.</label
              >
              <VueDatePicker v-model="form.expDate" month-picker auto-apply />
            </div>
            <div class="w-1/2">
              <label class="text-neutral-800 font-bold text-sm mb-2 block"
                >CVV:</label
              >
              <input
                type="text"
                v-model="form.cvv"
                @focus="isBackVisible = true"
                @blur="isBackVisible = false"
                class="flex h-10 w-full rounded-md border-2 bg-background px-4 py-1.5 text-lg ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:border-purple-600 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                :maxlength="cvvLength"
                placeholder="123"
              />
            </div>
          </div>
          <div class="w-full">
            <label class="text-neutral-800 font-bold text-sm mb-2 block"
              >Dueño tarjeta</label
            >
            <Combobox
              class="w-full"
              v-model="form.cardeableId"
              autocomplete="off"
            >
              <div class="relative">
                <div class="rounded border border-gray-300">
                  <ComboboxInput
                    class="w-full border-none pl-3 py-2 pr-10 text-sm rounded"
                    :value="form.cardeableId !== '' ? userName : query"
                    @change="query = $event.target.value"
                  />
                  <ComboboxButton
                    class="absolute inset-y-0 right-0 flex items-center pr-2"
                  >
                    <ChevronUpDownIcon
                      class="h-5 w-5 text-gray-400"
                      aria-hidden="true"
                    />
                  </ComboboxButton>
                </div>
                <TransitionRoot
                  leave="transition ease-in duration-100"
                  leave-from="opacityId-100"
                  leave-to="opacityId-0"
                  @after-leave="query = ''"
                >
                  <ComboboxOptions
                    class="absolute mt-1 max-h-60 w-full rounded-md bg-white py-1 text-base shadow-lg sm:text-sm overflow-y-auto bottom-full"
                  >
                    <div
                      v-if="filterUsers?.length === 0 && query !== ''"
                      class="cursor-default select-none py-2 px-4 text-gray-700"
                    >
                      No hay resultados
                    </div>

                    <ComboboxOption
                      v-for="user in filterUsers"
                      :key="user.id"
                      v-slot="{ selected, active }"
                      as="template"
                      :value="user"
                    >
                      <li
                        class="cursor-default select-none py-2 pl-5 flex justify-between"
                        :class="{
                          'bg-indigo-600 text-white': active,
                          'text-gray-900': !active,
                        }"
                      >
                        <span
                          class=""
                          :class="{
                            'font-bold': selected,
                            'font-normal': !selected,
                          }"
                        >
                          {{ user.name + " " + user.lastname }}
                        </span>

                        <span v-if="selected">
                          <CheckIcon class="h-5 w-5 mx-2" aria-hidden="true" />
                        </span>
                      </li>
                    </ComboboxOption>
                  </ComboboxOptions>
                </TransitionRoot>
              </div>
            </Combobox>
          </div>
        </div>
        <div class="my-5 flex justify-center w-1/2">
          <StyledButton
            :loading="createCardLoading"
            :disabled="createCardLoading"
            class="w-full rounded-lg bg-indigo-500 py-2 px-3 font-bold uppercase text-white hover:bg-indigo-700"
            >Cargar</StyledButton
          >
        </div>
      </form>
    </div>
  </div>
</template>

<style scoped>
.rotate-y-180 {
  transform: rotateY(180deg);
}

.rotate-cvv {
  transform: rotateY(180deg);
}

.cvv-input {
  transform: rotateY(-180deg);
}

.front,
.back {
  backface-visibility: hidden;
  position: absolute;
  top: 0;
  left: 0;
}

.seeBack .front {
  transform: rotateY(-180deg);
}

.seeBack .back {
  transform: rotateY(0deg);
}

.crediCard {
  transition: transform 0.5s;
}

.hidden {
  display: none;
}
</style>
